import type { AudioHTMLAttributes, DetailedHTMLProps } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import {
  useAudioPlayerRef,
  useAudioPlayerState,
} from '@trinity/chat-components/containers/AudioPlayer/Context';
import {
  useAudioPlayerScan,
  useBootstrapAudioPlayer,
  useToggleAudioPlaying,
} from '@trinity/chat-components/containers/AudioPlayer/hooks';
import { cx } from '@trinity/chat-components/utils';
import Button from './Button';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import Time from './Progress.Time';
import Track from './Progress.Track';
import styles from './style/Player.module.css';

type Props = {
  style?: {
    button?: React.CSSProperties;
    track?: React.CSSProperties;
  };
  color?: string;
  className?: string;
  audio: DetailedHTMLProps<AudioHTMLAttributes<HTMLAudioElement>, HTMLAudioElement>;
  clipStart?: number;
  clipEnd?: number;
  onPlay?: () => void;
};

export const AudioPlayer = ({ audio, className, style = {}, clipStart, clipEnd, onPlay }: Props) => {
  const [ref, setPlayerRef] = useAudioPlayerRef();
  const [{ duration, playing }] = useAudioPlayerState();

  const togglePlaying = useToggleAudioPlaying();
  const { scan, seek, position } = useAudioPlayerScan();
  const bootstrapAudioPlayer = useBootstrapAudioPlayer();

  const clipRange = useMemo(() => {
    if (!Number.isFinite(clipStart) || !Number.isFinite(clipEnd)) return null;

    const start = Math.max(0, Number(clipStart));
    const end = Math.max(0, Number(clipEnd));

    if (end <= start) return null;

    return {
      start,
      end,
      duration: end - start,
    };
  }, [clipEnd, clipStart]);

  const displayDuration = clipRange ? clipRange.duration : duration;
  const displayPosition = useMemo(() => {
    if (!clipRange) return position;

    if (position <= clipRange.start) return 0;
    if (position >= clipRange.end) return clipRange.duration;

    return position - clipRange.start;
  }, [clipRange, position]);

  const handleScan = useCallback(
    (time: number) => {
      scan(clipRange ? clipRange.start + time : time);
    },
    [clipRange, scan]
  );

  const handleSeek = useCallback(
    (time: number) => {
      seek(clipRange ? clipRange.start + time : time);
    },
    [clipRange, seek]
  );

  useKeyboardShortcuts();

  useEffect(() => {
    if (ref && onPlay) {
      ref.addEventListener('play', onPlay);
    }

    return () => {
      if (ref && onPlay) {
        ref.removeEventListener('play', onPlay);
      }
    };
  }, [ref, onPlay]);

  useEffect(() => {
    if (!ref || !clipRange) return;

    const resetToClipStart = () => {
      if (ref.currentTime < clipRange.start || ref.currentTime >= clipRange.end) {
        ref.currentTime = clipRange.start;
      }
    };

    const pauseAtClipEnd = () => {
      if (ref.currentTime >= clipRange.end) {
        ref.currentTime = clipRange.end;
        ref.pause();
      }
    };

    resetToClipStart();
    ref.addEventListener('play', resetToClipStart);
    ref.addEventListener('timeupdate', pauseAtClipEnd);

    return () => {
      ref.removeEventListener('play', resetToClipStart);
      ref.removeEventListener('timeupdate', pauseAtClipEnd);
    };
  }, [clipRange, ref]);

  return (
    <>
      <div className={cx(styles.root, className)}>
        <Button style={style.button} playing={playing} togglePlaying={togglePlaying} />
        <Track
          style={style.track}
          duration={displayDuration}
          position={displayPosition}
          scan={handleScan}
          seek={handleSeek}
        />
        <Time className={styles.time} currentTime={displayPosition} duration={displayDuration} />
      </div>
      <audio ref={setPlayerRef} {...bootstrapAudioPlayer()} {...audio} />
    </>
  );
};
